/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {useCallback, useEffect, useState} from 'react';
import GoogleMapReact from 'google-map-react';
import {config} from '../environment';
import Marker from './marker';
import Polyline from './polyline';
import {Spinner} from '@volkswagen-onehub/components-core';
import styled from 'styled-components';
import {PositionDetail, PositionDetailResponse} from '../types/vwCelebration';
import {useFetchVWCelebrationGPS} from '../hooks/useFetchVWCelebrationGPS';
import {isInIframe} from '../helpers/in-iframe';
import {GPSTourBranchConfig, GPSTourBrandExperiences} from '../constants';

type VWCelebrationMapProps = {
  gpsTourBranch?: GPSTourBrandExperiences;
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const LoaderOverlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: #f5f5f5;
  opacity: 0.5;
`;

const Loader = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const VWCelebrationMap: React.FC<VWCelebrationMapProps> = ({
  gpsTourBranch = GPSTourBrandExperiences.ID4,
}) => {
  const [map, setMap] = useState<google.maps.Map>();
  const [maps, setMaps] = useState<any>();
  const [selectedMarker, setSelectedMarker] = useState<string>();
  const [infoWindowProps, setInfoWindowProps] = useState<PositionDetail>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isInAEMeditor = isInIframe();

  const {event, keyword, centerPosition, defaultZoom} =
    GPSTourBranchConfig[gpsTourBranch];
  const {data: gpsData, loading: loadingGPS} = useFetchVWCelebrationGPS({
    event,
    keyword,
  });

  const closeInfoWindow = useCallback(() => {
    setSelectedMarker(undefined);
  }, []);

  const fetchDetailPostion = useCallback(
    async (id: string) => {
      try {
        setIsLoading(true);
        const data: PositionDetailResponse = await fetch(
          `${config.API_VW_CELEBRATION_POSITION_DETAIL}?id=${id}&keyword=${keyword}`,
        ).then((res) => res.json());

        setSelectedMarker(id);
        setInfoWindowProps(data.position);
      } catch (error) {
        console.error('Error fetching detail position', error);
      } finally {
        setIsLoading(false);
      }
    },
    [keyword],
  );

  const onClickMarker = useCallback(
    (id: string, childProps: any) => {
      fetchDetailPostion(id);
      map?.panTo({lat: childProps.lat, lng: childProps.lng});
    },
    [fetchDetailPostion, map],
  );

  const setLatestMarker = useCallback(() => {
    const position = gpsData?.positions[gpsData.positions.length - 1];
    const latestMarker = new maps.Marker({
      position: {lat: position?.t, lng: position?.g},
    });

    latestMarker.setMap(map);
  }, [gpsData?.positions, map, maps]);

  useEffect(() => {
    if (map && maps && gpsData) {
      setLatestMarker();
    }
  }, [gpsData, map, maps, setLatestMarker]);

  return (
    <Container>
      {isInAEMeditor ? null : (
        <GoogleMapReact
          bootstrapURLKeys={{
            key: config.GOOGLE_MAPS_CLIENT_API_KEY,
          }}
          defaultCenter={centerPosition}
          defaultZoom={defaultZoom}
          options={{
            mapTypeId: 'hybrid',
            fullscreenControl: false,
          }}
          onGoogleApiLoaded={({map, maps}) => {
            setMap(map);
            setMaps(maps);
          }}
          onChildClick={onClickMarker}
        >
          {gpsData?.positions.map((item) => {
            const shouldOpenInfoWindow = String(item.id) === selectedMarker;

            return (
              <Marker
                key={item.id}
                infoWindow={infoWindowProps}
                shouldOpenInfoWindow={shouldOpenInfoWindow}
                lat={item.t}
                lng={item.g}
                closeInfoWindow={closeInfoWindow}
              />
            );
          })}
          <Polyline map={map} maps={maps} gpsData={gpsData} />
        </GoogleMapReact>
      )}
      {(isLoading || loadingGPS) && (
        <LoaderOverlay>
          <Loader>
            <Spinner variant="large" />
          </Loader>
        </LoaderOverlay>
      )}
    </Container>
  );
};

export default VWCelebrationMap;
